import React from 'react';
import { Button } from 'antd';
import { styled } from 'styled-components';

import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAppDispatch } from '../../../shared/hooks/use-app-dispatch';
import { changeLocation } from '../../../actions';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { t } from '../../../utils/i18n';
import { useBrandLogos } from './company-logos';
import { TOUCH_TARGET_SIZE } from '../touch-target';

/**
 * Below this the full lockup no longer fits beside the menu button, the search
 * button and the avatar, so the bar falls back to the mark alone. 360px is the
 * narrowest width in common use (Galaxy S, Pixel a-series); the lockup clears it
 * with room to spare and the mark covers everything below.
 */
const FULL_LOGO_MIN_WIDTH = 360;

const StyledLogoButton = styled(Button)`
  /* Doubled selector so this outranks antd's own .ant-btn width rules. The box
     grows with the lockup but never shrinks below a finger. */
  && {
    height: ${TOUCH_TARGET_SIZE}px;
    min-width: ${TOUCH_TARGET_SIZE}px;
    width: auto;
    padding: 0 6px;
    flex-shrink: 0;
  }
`;

const StyledMark = styled.span`
  display: inline-flex;

  @media (min-width: ${FULL_LOGO_MIN_WIDTH}px) {
    display: none;
  }
`;

const StyledFullLogo = styled.span`
  display: none;

  @media (min-width: ${FULL_LOGO_MIN_WIDTH}px) {
    display: inline-flex;
  }
`;

/**
 * The brand in the mobile app bar. Below `md` the side menu lives in a drawer,
 * so nothing on screen identifies the product and the logo's go-home tap target
 * — which desktop has in the sider — is missing. This puts both back, using the
 * same lockup and size the side menu shows so the two read as one brand.
 */
export function HeaderBrandLogo() {
  const dispatch = useAppDispatch();
  const { trackMethod, eventBus } = useGlobals();
  const { FullLogo, LogoMark } = useBrandLogos();

  const handleOnClick = () => {
    const route = '/calendar';
    dispatch(changeLocation(route));
    trackMethod('header_logo_clicked', {
      platform: 'booking',
      location: 'header',
    });
    eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
  };

  return (
    <StyledLogoButton
      type="text"
      aria-label={t('header.go-to-home')}
      onClick={handleOnClick}
    >
      {/* Both render; the viewport picks one, so no re-render on resize. */}
      <StyledMark>
        <LogoMark />
      </StyledMark>
      <StyledFullLogo>
        <FullLogo />
      </StyledFullLogo>
    </StyledLogoButton>
  );
}
